import * as React from 'react';
import { formatNumber } from '@progress/kendo-intl';
import { Button } from '@progress/kendo-react-buttons';
import { Loader } from '@progress/kendo-react-indicators';
import { arrowRotateCwIcon } from '@progress/kendo-svg-icons';
import PopupNavBar from 'components/Layout/PopupNavBar';
import { fetchApi } from 'services/api';
import { Title } from 'utils/title';
import { Grid, GridColumn as Column, GridCustomFooterCellProps } from '@progress/kendo-react-grid';
import { Link } from 'react-router-dom';
import { SvgIcon } from '@progress/kendo-react-common';
import { caretAltDownIcon, caretAltUpIcon } from '@progress/kendo-svg-icons';
import CorporateSalesDashboardVerticalDetail from './CorporateSalesDashboardVerticalDetail';

interface CustomerSummary {
  CustomerID: number;
  CustomerName: string;
  SalespersonAssignment: string;
  YesterdayRevenue: number;
  MonthToDateRevenue: number;
  YesterdayUnrealized: number;
  YesterdayStarted: number;
  YesterdayCompleted: number;
  MonthToDateUnrealized: number;
  MonthToDateStarted: number;
  MonthToDateCompleted: number;
}

interface SalespersonSummary {
  SalespersonID: number;
  Name: string;
  SalesCode: string;
  YesterdayRevenue: number;
  MonthToDateRevenue: number;
  YesterdayUnrealized: number;
  YesterdayStarted: number;
  YesterdayCompleted: number;
  MonthToDateUnrealized: number;
  MonthToDateStarted: number;
  MonthToDateCompleted: number;
  Customers: CustomerSummary[];
}

interface SummaryCard {
  Title: string;
  FormattedValue: string;
  SubFormattedValue1?: string;
  SubFormattedValue2?: string;
  SubFormattedValue3?: string;
  SubFormattedValue4?: string;
  PercentageChange?: number | null;
  Change?: string | null;
}

interface OnboardedVerticalSummary {
  IndustryVerticalId: number;
  IndustryVerticalName: string;
  YTDOnboardedAccounts: number;
  YTDActiveAccounts: number;
  YTDTotalRevenue: number;
  YTDConversionRate?: number | null;
  CurQOnboardedAccounts: number;
  CurQActiveAccounts: number;
  CurQTotalRevenue: number;
  CurQConversionRate?: number | null;
}

interface CorporateSalesDashboardResponse {
  Salespeople: SalespersonSummary[];
  TennesseeSalespeople: SalespersonSummary[];
  TotalMonthToDateRevenue: number;
  TotalYesterdayRevenue: number;
  RevenueCards: SummaryCard[];
  OnboardedVerticals: OnboardedVerticalSummary[];
}

const CorporateSalesDashboard: React.FC = () => {
  const [data, setData] = React.useState<CorporateSalesDashboardResponse | null>(null);
  const [loading, setLoading] = React.useState<boolean>(false);

  const loadDashboard = React.useCallback(() => {
    setLoading(true);
    fetchApi('/api/Customer/CorporateSalesDashboard')
      .then((response: CorporateSalesDashboardResponse) => setData(response))
      .catch(() => alert('Unable to load the sales dashboard. Please try again.'))
      .finally(() => setLoading(false));
  }, []);

  const SumFooterCell = (props: GridCustomFooterCellProps) => {
    if (!data) return <td {...props.tdProps}>{formatNumber(0, 'c')}</td>;

    const sum = (selector: (sp: SalespersonSummary) => number) =>
      data.Salespeople.reduce((acc: number, sp: SalespersonSummary) => acc + (selector(sp) || 0), 0);

    switch(props.field)
    {
      case "YesterdayRevenue":
        return <td {...props.tdProps}>{formatNumber(sum((sp) => sp.YesterdayRevenue), 'c')}</td>;
      case "YesterdayUnrealized":
        return <td {...props.tdProps}>{formatNumber(sum((sp) => sp.YesterdayUnrealized), 'c')}</td>;
      case "YesterdayStarted":
        return <td {...props.tdProps}>{formatNumber(sum((sp) => sp.YesterdayStarted), 'c')}</td>;
      case "YesterdayCompleted":
        return <td {...props.tdProps}>{formatNumber(sum((sp) => sp.YesterdayCompleted), 'c')}</td>;
      case "MonthToDateRevenue":
        return <td {...props.tdProps}>{formatNumber(sum((sp) => sp.MonthToDateRevenue), 'c')}</td>;
      case "MonthToDateUnrealized":
        return <td {...props.tdProps}>{formatNumber(sum((sp) => sp.MonthToDateUnrealized), 'c')}</td>;
      case "MonthToDateStarted":
        return <td {...props.tdProps}>{formatNumber(sum((sp) => sp.MonthToDateStarted), 'c')}</td>;
      case "MonthToDateCompleted":
        return <td {...props.tdProps}>{formatNumber(sum((sp) => sp.MonthToDateCompleted), 'c')}</td>;
    }
  }

  const TennesseeSumFooterCell = (props: GridCustomFooterCellProps) => {
    if (!data) return <td {...props.tdProps}>{formatNumber(0, 'c')}</td>;

    const sum = (selector: (sp: SalespersonSummary) => number) =>
      data.TennesseeSalespeople.reduce((acc: number, sp: SalespersonSummary) => acc + (selector(sp) || 0), 0);

    switch(props.field)
    {
      case "YesterdayRevenue":
        return <td {...props.tdProps}>{formatNumber(sum((sp) => sp.YesterdayRevenue), 'c')}</td>;
      case "YesterdayUnrealized":
        return <td {...props.tdProps}>{formatNumber(sum((sp) => sp.YesterdayUnrealized), 'c')}</td>;
      case "YesterdayStarted":
        return <td {...props.tdProps}>{formatNumber(sum((sp) => sp.YesterdayStarted), 'c')}</td>;
      case "YesterdayCompleted":
        return <td {...props.tdProps}>{formatNumber(sum((sp) => sp.YesterdayCompleted), 'c')}</td>;
      case "MonthToDateRevenue":
        return <td {...props.tdProps}>{formatNumber(sum((sp) => sp.MonthToDateRevenue), 'c')}</td>;
      case "MonthToDateUnrealized":
        return <td {...props.tdProps}>{formatNumber(sum((sp) => sp.MonthToDateUnrealized), 'c')}</td>;
      case "MonthToDateStarted":
        return <td {...props.tdProps}>{formatNumber(sum((sp) => sp.MonthToDateStarted), 'c')}</td>;
      case "MonthToDateCompleted":
        return <td {...props.tdProps}>{formatNumber(sum((sp) => sp.MonthToDateCompleted), 'c')}</td>;
    }
  }

  React.useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  return (
    <React.Fragment>
      <PopupNavBar />
      <Title string="Sales Dashboard" />
      <nav className="navbar navbar-dark bg-dark">
        <a className="navbar-brand" href="/">Sales Dashboard {loading && <Loader type="pulsing" className="p-0" />}</a>
        <ul className="nav navbar-nav navbar-right">
          <Button
            title="Refresh"
            icon="refresh"
            svgIcon={arrowRotateCwIcon}
            disabled={loading}
            onClick={loadDashboard}
          />
        </ul>
      </nav>
      <div className="container-fluid">
        <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-5">
          {data?.RevenueCards.map((card, index) => {
            const percentage = typeof card.PercentageChange === 'number' ? card.PercentageChange : null;
            return (
              <div key={`revenue-${index}`} className="col mt-3">
                <div className="card h-100">
                  <div className="card-body">
                    <h5 className="text-muted fw-normal mt-0">{card.Title}</h5>
                    <h3 className="mt-3 mb-3">{card.FormattedValue}</h3>
                    {card.SubFormattedValue1 && <p className="mb-1">{card.SubFormattedValue1}</p>}
                    {card.SubFormattedValue2 && <p className="mb-1">{card.SubFormattedValue2}</p>}
                    {card.SubFormattedValue3 && <p className="mb-1">{card.SubFormattedValue3}</p>}
                    {card.SubFormattedValue4 && <p className="mb-1">{card.SubFormattedValue4}</p>}
                    {(percentage !== null || card.Change) && (
                      <p className="mb-0 text-muted">
                        {percentage !== null && (
                          <span className={`mr-2 ${percentage > 0 ? 'text-success' : percentage < 0 ? 'text-danger' : ''}`}>
                            <SvgIcon icon={percentage > 0 ? caretAltUpIcon : percentage < 0 ? caretAltDownIcon : undefined} /> {formatNumber(percentage, 'p2')}
                          </span>
                        )}
                        {card.Change && <span className="text-nowrap">{card.Change}</span>}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <h5 className="mt-4">Corporate Salespeople</h5>
        <Grid
          data={data?.Salespeople || []}
          dataItemKey="SalespersonID"
          showLoader={loading}
          detail={(props) => <Grid data={props.dataItem.Customers} dataItemKey="CustomerID">
            <Column field="CustomerName" title="Customer" cells={{ data: (e) => <td {...e.tdProps}>
              <Link style={{ color: "#007bff" }} to={`/Customers/Customer/${e.dataItem.CustomerID}`}>{e.dataItem.CustomerName}</Link>
            </td> }} />
            <Column field="SalespersonAssignment" title="Assignment" />
            <Column field="YesterdayRevenue" title="Yesterday" format="{0:c}" />
            <Column field="MonthToDateRevenue" title="MTD Revenue" format="{0:c}" />
            <Column field="YesterdayUnrealized" title="Yesterday Unrealized" format="{0:c}" />
            <Column field="YesterdayStarted" title="Yesterday Started" format="{0:c}" />
            <Column field="YesterdayCompleted" title="Yesterday Completed" format="{0:c}" />
            <Column field="MonthToDateUnrealized" title="MTD Unrealized" format="{0:c}" />
            <Column field="MonthToDateStarted" title="MTD Started" format="{0:c}" />
            <Column field="MonthToDateCompleted" title="MTD Completed" format="{0:c}" />
          </Grid>}
        >
          <Column field="Name" title="Salesperson" cells={{ data: (props) => <td {...props.tdProps}><Link style={{ color: "#007bff" }} to={`/SalesReports/${props.dataItem.SalespersonID}`}>{props.dataItem.Name}</Link></td> }} />
          <Column field="YesterdayRevenue" title="Yesterday Revenue" format="{0:c}" cells={{ footerCell: SumFooterCell }} />
          <Column field="MonthToDateRevenue" title="MTD Revenue" format="{0:c}" cells={{ footerCell: SumFooterCell }} />
          <Column field="YesterdayUnrealized" title="Yesterday Unrealized" format="{0:c}" cells={{ footerCell: SumFooterCell }} />
          <Column field="YesterdayStarted" title="Yesterday Started" format="{0:c}" cells={{ footerCell: SumFooterCell }} />
          <Column field="YesterdayCompleted" title="Yesterday Completed" format="{0:c}" cells={{ footerCell: SumFooterCell }} />
          <Column field="MonthToDateUnrealized" title="MTD Unrealized" format="{0:c}" cells={{ footerCell: SumFooterCell }} />
          <Column field="MonthToDateStarted" title="MTD Started" format="{0:c}" cells={{ footerCell: SumFooterCell }} />
          <Column field="MonthToDateCompleted" title="MTD Completed" format="{0:c}" cells={{ footerCell: SumFooterCell }} />
        </Grid>
        <h5 className="mt-4">Tennessee Salespeople</h5>
        <Grid
          data={data?.TennesseeSalespeople || []}
          dataItemKey="SalespersonID"
          showLoader={loading}
          detail={(props) => <Grid data={props.dataItem.Customers} dataItemKey="CustomerID">
            <Column field="CustomerName" title="Customer" cells={{ data: (e) => <td {...e.tdProps}>
              <Link style={{ color: "#007bff" }} to={`/Customers/Customer/${e.dataItem.CustomerID}`}>{e.dataItem.CustomerName}</Link>
            </td> }} />
            <Column field="SalespersonAssignment" title="Assignment" />
            <Column field="YesterdayRevenue" title="Yesterday" format="{0:c}" />
            <Column field="MonthToDateRevenue" title="MTD Revenue" format="{0:c}" />
            <Column field="YesterdayUnrealized" title="Yesterday Unrealized" format="{0:c}" />
            <Column field="YesterdayStarted" title="Yesterday Started" format="{0:c}" />
            <Column field="YesterdayCompleted" title="Yesterday Completed" format="{0:c}" />
            <Column field="MonthToDateUnrealized" title="MTD Unrealized" format="{0:c}" />
            <Column field="MonthToDateStarted" title="MTD Started" format="{0:c}" />
            <Column field="MonthToDateCompleted" title="MTD Completed" format="{0:c}" />
          </Grid>}
        >
          <Column field="Name" title="Salesperson" cells={{ data: (props) => <td {...props.tdProps}><Link style={{ color: "#007bff" }} to={`/SalesReports/${props.dataItem.SalespersonID}`}>{props.dataItem.Name}</Link></td> }} />
          <Column field="YesterdayRevenue" title="Yesterday Revenue" format="{0:c}" cells={{ footerCell: TennesseeSumFooterCell }} />
          <Column field="MonthToDateRevenue" title="MTD Revenue" format="{0:c}" cells={{ footerCell: TennesseeSumFooterCell }} />
          <Column field="YesterdayUnrealized" title="Yesterday Unrealized" format="{0:c}" cells={{ footerCell: TennesseeSumFooterCell }} />
          <Column field="YesterdayStarted" title="Yesterday Started" format="{0:c}" cells={{ footerCell: TennesseeSumFooterCell }} />
          <Column field="YesterdayCompleted" title="Yesterday Completed" format="{0:c}" cells={{ footerCell: TennesseeSumFooterCell }} />
          <Column field="MonthToDateUnrealized" title="MTD Unrealized" format="{0:c}" cells={{ footerCell: TennesseeSumFooterCell }} />
          <Column field="MonthToDateStarted" title="MTD Started" format="{0:c}" cells={{ footerCell: TennesseeSumFooterCell }} />
          <Column field="MonthToDateCompleted" title="MTD Completed" format="{0:c}" cells={{ footerCell: TennesseeSumFooterCell }} />
        </Grid>
        <h5 className="mt-4">Fiscal YTD and Current Quarter Onboarded by Vertical</h5>
        <Grid
          data={data?.OnboardedVerticals || []}
          dataItemKey="IndustryVerticalId"
          showLoader={loading}
          detail={(props) => <CorporateSalesDashboardVerticalDetail {...props} />}
        >
          <Column field="IndustryVerticalName" title="Vertical" />
          <Column field="YTDOnboardedAccounts" title="YTD Onboarded" />
          <Column field="YTDActiveAccounts" title="YTD Active" />
          <Column field="YTDTotalRevenue" title="YTD Revenue" format="{0:c}" />
          <Column field="YTDConversionRate" title="YTD Conversion" format="{0:p0}" />
          <Column field="CurQOnboardedAccounts" title="Q Onboarded" />
          <Column field="CurQActiveAccounts" title="Q Active" />
          <Column field="CurQTotalRevenue" title="Q Revenue" format="{0:c}" />
          <Column field="CurQConversionRate" title="Q Conversion" format="{0:p0}" />
        </Grid>
        <br />
      </div>
    </React.Fragment>
  );
};

export default CorporateSalesDashboard;
